import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGetKeywordThemes } from "@/resources/keyword-theme";
import { AddThemeModal } from "@/components/keyword-bank/AddThemeModal";
import { DEFAULT_TABLE_PAGE_SIZE, TablePagination, usePagination } from "@/components/shared/TablePagination";
import type { Keyword } from "@/types/keyword";
import { KeywordBankPickerSearch } from "./KeywordBankPickerSearch";
import { KeywordBankPickerTable } from "./KeywordBankPickerTable";
import {
  selectBankPickerRows,
  type BankPickerListFilter,
  type BankPickerRow,
  type BankPickerSort,
} from "./select-bank-picker-rows";

export interface KeywordBankPickerProps {
  productId: number;
  /** The ASIN row actions (Add Landscape, Add to Rank Tracker) act against. */
  heroAsin: string;
  /** The hero product's keyword bank, `productApi.useGetHeroProductKeywords`'s `data`. */
  keywords: Keyword[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isApplying: boolean;
  onPickKeyword: (row: BankPickerRow) => void;
}

const SKELETON_ROW_COUNT = 8;

const TABLE_HEADERS = [
  "Keywords",
  "Hero Rank",
  "KPS",
  "Kw. Sales",
  "Search Vol.",
  "ASIN Density",
  "Comp.",
  "Rel.",
  "Trend",
  "Theme",
  "Utilization",
  "Source",
  "Actions",
];

function toTrend(raw: Keyword["search_volume_trend"]): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

/** `KeywordBankContentV2`'s own join, keyed the same way: keyword id -> theme name. */
function buildThemeByKeywordId(themes: { id: number; name: string; keywords: { keyword_id: number }[] }[]) {
  const map = new Map<number, string>();
  for (const theme of themes) {
    for (const tk of theme.keywords) {
      map.set(tk.keyword_id, theme.name);
    }
  }
  return map;
}

function buildBankPickerRows(keywords: Keyword[], themeByKeywordId: Map<number, string>): BankPickerRow[] {
  return keywords.map((keyword) => ({
    keywordId: keyword.id,
    keyword: keyword.search,
    rank: keyword.hero_rank,
    searchVolume: keyword.search_volume,
    isMaster: keyword.is_master_list ?? false,
    theme: themeByKeywordId.get(keyword.id) ?? null,
    kps: keyword.kps,
    competition: keyword.competitors,
    relevance: keyword.hero_relevance,
    trend: toTrend(keyword.search_volume_trend),
    source: keyword,
  }));
}

/**
 * Assembles the A/B creation Keywords tab (`components/ab-test.md § Target state — AUT-430 spec
 * C` → *The Keywords tab*): the search bar with its typeahead, the bank table (`WEB-006`), the
 * `AddThemeModal` mount, and this tab's own loading / empty / error states. Does not render the
 * `Keyword Bank` title -- the host page does (`WEB-011`).
 *
 * `useGetKeywordThemes(productId)` is called exactly once, here; its `data ?? []` feeds the row ->
 * theme join below, the table's `themes` prop, and `AddThemeModal`'s `existingThemesCount`
 * (*Threading*).
 */
export function KeywordBankPicker({
  productId,
  heroAsin,
  keywords,
  isLoading,
  isError,
  onRetry,
  isApplying,
  onPickKeyword,
}: KeywordBankPickerProps) {
  const [query, setQuery] = useState("");
  const [list, setList] = useState<Set<BankPickerListFilter>>(new Set());
  const [themeFilter, setThemeFilter] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<BankPickerSort>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [searchBarHeight, setSearchBarHeight] = useState(0);
  const [addThemeOpen, setAddThemeOpen] = useState(false);
  const [addThemeKeywordId, setAddThemeKeywordId] = useState<number | null>(null);

  const { data: themesData } = useGetKeywordThemes(productId);
  const themes = useMemo(() => themesData ?? [], [themesData]);

  const themeByKeywordId = useMemo(() => buildThemeByKeywordId(themes), [themes]);

  const allRows = useMemo(
    () => buildBankPickerRows(keywords, themeByKeywordId),
    [keywords, themeByKeywordId],
  );

  const themeOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of allRows) {
      if (row.theme !== null) names.add(row.theme);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  const pickerRows = useMemo(
    () => selectBankPickerRows(allRows, { query, list, themes: themeFilter, sort }),
    [allRows, query, list, themeFilter, sort],
  );

  const { paginatedItems: visibleRows, safePage } = usePagination(pickerRows, page, pageSize);

  // Changing what narrows or orders the set returns to page 0 -- a stale page
  // number past the new (smaller or reordered) result would otherwise show an
  // arbitrary slice. A pick (row click) touches none of these, so it never
  // resets the page. Mirrors `KeywordBankContentV2`'s reset effect.
  useEffect(() => {
    setPage(0);
  }, [query, list, themeFilter, sort]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const handleOpenAddTheme = (keywordId: number) => {
    setAddThemeKeywordId(keywordId);
    setAddThemeOpen(true);
  };

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 px-4 text-center">
        <p className="text-sm text-destructive font-medium">Could not load keywords</p>
        <p className="text-xs text-muted-foreground max-w-md">
          Request failed. Check the network tab or try again.
        </p>
        <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Table stickyHeader={false} className="min-w-[1200px]">
        <TableHeader>
          <TableRow>
            {TABLE_HEADERS.map((header) => (
              <TableHead key={header} className="text-xs font-semibold text-foreground">
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
            <TableRow key={idx} data-testid="keyword-bank-picker-skeleton-row">
              {TABLE_HEADERS.map((header) => (
                <TableCell key={header}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (keywords.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-border bg-card px-6 text-center min-h-0 py-12">
        <div className="space-y-1.5">
          <p className="text-base font-semibold text-foreground">No tracked keywords yet</p>
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">
            Add this ASIN to the Rank Tracker to surface keyword opportunities here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <KeywordBankPickerSearch
        query={query}
        onQueryChange={setQuery}
        list={list}
        onListChange={setList}
        themeFilter={themeFilter}
        onThemeFilterChange={setThemeFilter}
        themeOptions={themeOptions}
        rows={allRows}
        onPickSuggestion={(row) => setQuery(row.keyword)}
        onHeightChange={setSearchBarHeight}
      />
      <div className="flex-1 min-h-0 w-full min-w-0 border-t border-border">
        <KeywordBankPickerTable
          productId={productId}
          heroAsin={heroAsin}
          rows={visibleRows}
          sort={sort}
          onSortChange={setSort}
          onPickKeyword={onPickKeyword}
          isApplying={isApplying}
          stickyHeaderOffset={searchBarHeight}
          onOpenAddTheme={handleOpenAddTheme}
          themes={themes.map((theme) => ({ id: theme.id, name: theme.name }))}
        />
      </div>
      {pickerRows.length > 0 ? (
        <div className="shrink-0 bg-card">
          <TablePagination
            totalItems={pickerRows.length}
            page={safePage}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      ) : null}
      <AddThemeModal
        open={addThemeOpen}
        onOpenChange={setAddThemeOpen}
        productId={productId}
        allKeywords={keywords}
        existingThemesCount={themes.length}
        initialKeywordId={addThemeKeywordId}
      />
    </div>
  );
}
